/**
 * #933 PR 3 — Settings → Services & prices.
 *
 * The tenant's own catalog — what customers see, what they pay, how long it
 * takes, and whether it is offered — edited by the owner instead of by
 * `seed-hthd-catalog.ts` from a session. Ted's A3 ("walk prices") lands
 * here; so does "Ted adds Walk 30 min $30 and it is bookable with its own
 * capacity" (spec §7).
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: one row per service leading with the name
 *    customers see, then the price, the duration, whether it is offered and
 *    the daily limit — each from the row the booking page reads. The engine
 *    key (`walking`) is never shown raw; the picker shows labels.
 *  - #2 The next action is obvious: one primary "Add a service"; per row an
 *    Edit that opens inline, a separate "Change price" (its own permission
 *    and its own route), and Hide as a two-tap with the consequence spelled
 *    out ("existing bookings are unaffected"). Non-owners see the catalog
 *    with a plain reason.
 *  - #5 Never lie about data: "Not set" for a missing limit is named as
 *    such; the create form asks for the limit up front (D3) so a new
 *    service is never unbookable by omission; a duplicate answer from the
 *    server names the row that already exists.
 *  - Five states: loading · error(+retry) · empty (no services — teaching
 *    copy) · ready · saving (per-row / per-form in-flight lock).
 *
 * Counter Test: Ted adds "Walk 30 min $30" with a limit of 10 from his
 * phone; the customer app lists it, priced, and refuses the 11th walk.
 *
 * Ted #17 (#921): each row shows the service's color as a word (or "No
 * color"); Color opens a picker of ten ≥ 44 px swatches + "No color", a pick
 * is ONE PATCH `{ color }` on the general route, and the row previews the
 * color it will wear on the daily rollup, the day list and the arrivals
 * list. Never a hex; never another service's color by default.
 *
 * Ted #1208 (Item 36): the Grooming row carries "Grooming menu and
 * prices", which opens the grooming wizard's own menu inline — its grooming
 * services with a price per dog size, and the add-ons with "Clients can book
 * this" (off = staff-only, e.g. Walk, House Food). See GroomingMenuEditor.
 * Every role can open it; only the pricing permission edits it.
 *
 * Kate K7/K9 (beta walkthrough, 2026-10-12): "Assessment" and "Adventure
 * hike" showed no description on the booking page — "what is it? what days?
 * how long is the hike? how many dogs?" — because only the owner can know,
 * and the owner had nowhere to write it. Each row now carries a description
 * for clients (edit + create); a row without one says so, since the customer
 * app then shows a generic line. And because Kate also found "dental
 * brushing" beside "toothbrushing" (K9): a name that looks like an existing
 * one gets a SOFT warning — "Looks like 'X' already exists; keep both?" —
 * never a block, since two near names can both be real.
 *
 * Beta K7 (Ted's hikes, Tuesdays and Thursdays): "Days this runs" on the edit
 * form, Sun to Sat, all on by default. It says who it binds in plain words:
 * clients can only book the ticked days, the desk can book any day, and what
 * is already booked stays. Non-binding states show nothing: a row that runs
 * every day carries no fact, and on a server too old to know the field the
 * control is not drawn at all (no key is ever sent). A leg-only service is
 * never booked by a client, so it gets no control that would do nothing.
 *
 * Beta K19 (#1317): each row also says what a client is told about paying
 * after booking, and the owner can change it (ServicePaymentMessageField). The
 * card owns the one fetch for all rows: skeleton while it loads, one Retry if
 * it fails, and nothing at all on a server that predates the route.
 *
 * Server contract: GET/POST /api/v2/admin/tenant/service-types,
 * PATCH /service-types/:id, PATCH /service-types/:id/price; the grooming menu
 * is /api/v2/admin/tenant/grooming-menu (GroomingMenuEditor); the after-booking
 * message is GET /service-payment-messages + PUT /service-types/:id/payment-message.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, EmptyState, Input, Modal, Select, Skeleton } from '@ferro/ui';
import {
  adminTenantServicesApi,
  type TenantCreatableService,
  type TenantServiceDraft,
  type TenantServicePatch,
  type TenantServiceView,
  type TenantServicesView,
} from '../../lib/api.js';
import {
  centsToDollarInput,
  describeWeekdays,
  dollarInputToCents,
  formatDollars,
  hoursInputToMinutes,
  intInput,
  minutesToHoursInput,
  WEEKDAY_SHORT,
} from './capacity-helpers.js';
import { runDaysFromStored, WEEKDAY_NUMBERS } from '@ferro/core/modules/booking/run-days.js';
import { isLegOnlyServiceName } from '@ferro/core/modules/booking/transport-services.js';
import {
  SERVICE_COLORS,
  SERVICE_COLOR_KEYS,
  isServiceColorKey,
  serviceColorClass,
} from '@ferro/core/modules/tenant-settings/service-colors.js';
import { findNearDuplicateName, nearDuplicateQuestion } from '@ferro/core/modules/tenant-settings/service-name.js';
import '../../service-colors.css';
import { GroomingMenuEditor } from './GroomingMenuEditor.js';
import { ServicePaymentMessageField } from './ServicePaymentMessageField.js';
import type { ServicePaymentMessageEntry } from '../../lib/service-payment-messages-api.js';
import { useServicePaymentMessages } from '../../lib/useServicePaymentMessages.js';

/** The engine key whose row opens the grooming menu (Ted #1208). */
export const GROOMING_SERVICE_NAME = 'grooming';

interface ServicesSettingsCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager) — names, durations, order, hide/show. */
  canEdit: boolean;
  /** Holds business-config.pricing:write:all (owner) — prices, and adding a service (it sets one). */
  canEditPrice: boolean;
}

type Status = 'loading' | 'error' | 'ready';

export const MAX_SERVICE_CAPACITY = 5000;
/** Kate K7/K9 — mirrors `SERVICE_DESCRIPTION_MAX` on the server. */
export const MAX_SERVICE_DESCRIPTION = 500;
export const DESCRIPTION_HELP = 'Shown to clients on the booking page. What it is, how long, what to bring, which days.';

export interface ServiceEditForm {
  displayName: string;
  /** Kate K7/K9 — blank = no description (clients see a generic line). */
  description: string;
  /** Blank = whole day. */
  duration: string;
  sortOrder: string;
  /** Hours; blank = no minimum. */
  notice: string;
  /** Days; blank = no limit. */
  window: string;
  /** Beta K7 — the ticked days. Absent when the server has no such field. */
  runDays?: number[];
}

export function editFormFromService(s: TenantServiceView): ServiceEditForm {
  return {
    displayName: s.displayName,
    description: s.description ?? '',
    duration: s.durationMinutes === null ? '' : String(s.durationMinutes),
    sortOrder: String(s.sortOrder),
    notice: minutesToHoursInput(s.minLeadTimeMinutes),
    window: s.maxAdvanceDays === null ? '' : String(s.maxAdvanceDays),
    // Beta K7 — `undefined` is an older server; null is every day, all ticked.
    ...(s.runDays === undefined ? {} : { runDays: [...(s.runDays ?? WEEKDAY_NUMBERS)] }),
  };
}

/** Beta K7 — the server's own words for the empty list, for the same refusal. */
export const RUN_DAYS_REQUIRED = 'Tick at least one day. To stop offering a service, hide it.';

/** Only the keys whose value differs from the row — the PATCH is a patch. */
export function patchFromEditForm(
  f: ServiceEditForm,
  s: TenantServiceView,
): { ok: true; patch: TenantServicePatch } | { ok: false; message: string } {
  const fail = (message: string) => ({ ok: false as const, message });
  const patch: TenantServicePatch = {};
  const displayName = f.displayName.trim();
  if (displayName.length === 0 || displayName.length > 80) return fail('Give the service a name customers will see (up to 80 characters).');
  if (displayName !== s.displayName) patch.displayName = displayName;
  const description = f.description.trim();
  if (description.length > MAX_SERVICE_DESCRIPTION) return fail(`Keep the description under ${MAX_SERVICE_DESCRIPTION} characters.`);
  // Blank clears (null), and an older server without the field ignores the key.
  if ((description === '' ? null : description) !== (s.description ?? null)) patch.description = description === '' ? null : description;
  const duration = intInput(f.duration, 5, 24 * 60);
  if (duration === undefined) return fail('Duration must be between 5 and 1440 minutes, or blank for a whole day.');
  if (duration !== s.durationMinutes) patch.durationMinutes = duration;
  const sortOrder = intInput(f.sortOrder, 0, 999);
  if (sortOrder === null || sortOrder === undefined) return fail('Order must be a whole number between 0 and 999.');
  if (sortOrder !== s.sortOrder) patch.sortOrder = sortOrder;
  const notice = hoursInputToMinutes(f.notice);
  if (notice === undefined || (notice !== null && notice > 60 * 24 * 60)) return fail('Minimum notice must be a number of hours (up to 1440), or blank for none.');
  if (notice !== s.minLeadTimeMinutes) patch.minLeadTimeMinutes = notice;
  const window = intInput(f.window, 1, 730);
  if (window === undefined) return fail('Booking window must be between 1 and 730 days, or blank for no limit.');
  if (window !== s.maxAdvanceDays) patch.maxAdvanceDays = window;
  if (f.runDays !== undefined) {
    if (f.runDays.length === 0) return fail(RUN_DAYS_REQUIRED);
    const ticked = runDaysFromStored(f.runDays);
    if (JSON.stringify(ticked) !== JSON.stringify(s.runDays ?? null)) patch.runDays = ticked === null ? null : [...ticked];
  }
  return { ok: true, patch };
}

export interface ServiceCreateForm {
  name: string;
  displayName: string;
  /** Kate K7/K9 — optional at create. */
  description: string;
  price: string;
  duration: string;
  capacity: string;
}

export const EMPTY_CREATE_FORM: ServiceCreateForm = { name: '', displayName: '', description: '', price: '', duration: '', capacity: '' };

export function draftFromCreateForm(
  f: ServiceCreateForm,
  creatable: ReadonlyArray<TenantCreatableService>,
): { ok: true; draft: TenantServiceDraft } | { ok: false; message: string } {
  const fail = (message: string) => ({ ok: false as const, message });
  const option = creatable.find((c) => c.name === f.name);
  if (!option) return fail('Pick the kind of service.');
  if (option.alreadyAdded) return fail(`${option.label} is already in your catalog — edit it in the list instead.`);
  const displayName = f.displayName.trim();
  if (displayName.length === 0 || displayName.length > 80) return fail('Give the service a name customers will see (up to 80 characters).');
  const basePriceCents = dollarInputToCents(f.price);
  if (basePriceCents === null) return fail('Enter the price in dollars, for example 30 or 30.50.');
  const duration = intInput(f.duration, 5, 24 * 60);
  if (duration === undefined) return fail('Duration must be between 5 and 1440 minutes, or blank for a whole day.');
  const draft: TenantServiceDraft = { name: f.name, displayName, basePriceCents, durationMinutes: duration };
  const description = f.description.trim();
  if (description.length > MAX_SERVICE_DESCRIPTION) return fail(`Keep the description under ${MAX_SERVICE_DESCRIPTION} characters.`);
  if (description.length > 0) draft.description = description;
  // Ted #28 Phase 1b — a leg-only service (transport) is never booked on its
  // own, so no daily limit is asked; the server writes no rule for it.
  if (!option.pooled && !option.legOnly) {
    const capacity = intInput(f.capacity, 0, MAX_SERVICE_CAPACITY);
    if (capacity === null || capacity === undefined) return fail(`Set the daily limit (0–${MAX_SERVICE_CAPACITY}) — without one customers could never book it.`);
    draft.capacity = capacity;
  }
  return { ok: true, draft };
}

export function durationLabel(minutes: number | null): string {
  if (minutes === null) return 'Whole day';
  if (minutes % 60 === 0) return `${minutes / 60} hr`;
  return `${minutes} min`;
}

/**
 * Kate K7/K9 — the description for clients, on the edit and the create
 * form alike. A textarea (three lines is a paragraph, which is what Kate
 * asked for), labelled and bound, with the same helper on both forms.
 */
function DescriptionField({ id, value, onChange, testId }: { id: string; value: string; onChange: (v: string) => void; testId: string }): JSX.Element {
  return (
    <div className="ferro-field ferro-services__description-field">
      <label htmlFor={id} className="ferro-field__label">
        Description for clients
      </label>
      <textarea
        id={id}
        className="ferro-field__input ferro-input ferro-services__description"
        rows={3}
        maxLength={MAX_SERVICE_DESCRIPTION}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="A two-hour trail hike on Tuesdays and Thursdays, up to six dogs. Bring a harness."
        aria-describedby={`${id}-help`}
        data-testid={testId}
      />
      <p id={`${id}-help`} className="ferro-field__helper">
        {DESCRIPTION_HELP}
      </p>
    </div>
  );
}

/**
 * Beta K7 — "Days this runs". The settings day chips (AssessmentScheduleCard's
 * "Days offered"), Sun to Sat so it reads like the client's calendar. The
 * helper is the whole contract in the owner's words.
 */
function RunDaysField({ id, days, onChange, disabled }: { id: string; days: readonly number[]; onChange: (next: number[]) => void; disabled: boolean }): JSX.Element {
  const toggle = (d: number) => onChange(days.includes(d) ? days.filter((x) => x !== d) : [...days, d]);
  return (
    <fieldset className="ferro-settings__fieldset ferro-services__run-days" aria-describedby={`${id}-help`} data-testid={id}>
      <legend className="ferro-field__label">Days this runs</legend>
      <div className="ferro-settings__daychips">
        {WEEKDAY_NUMBERS.map((d) => (
          <button
            key={d}
            type="button"
            className="ferro-settings__daychip"
            aria-pressed={days.includes(d)}
            disabled={disabled}
            onClick={() => toggle(d)}
            data-testid={`${id}-${d}`}
          >
            {WEEKDAY_SHORT[d]}
          </button>
        ))}
      </div>
      <p id={`${id}-help`} className="ferro-field__helper">
        Clients can only book the days that are on, and a stay can't cross a day that's off. Your team can still book any day, and existing bookings stay as they are.
      </p>
    </fieldset>
  );
}

export function ServicesSettingsCard({ apiUrl, canEdit, canEditPrice }: ServicesSettingsCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<TenantServicesView | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ServiceEditForm | null>(null);
  const [priceEditId, setPriceEditId] = useState<string | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const priceInputRef = useRef<HTMLInputElement>(null);
  // Focus the price field when Change price opens it (or moves it to another row).
  useEffect(() => {
    if (priceEditId !== null) priceInputRef.current?.focus();
  }, [priceEditId]);
  /** #921 — the row whose color picker is open. */
  const [colorEditId, setColorEditId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [confirmHideId, setConfirmHideId] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<ServiceCreateForm>(EMPTY_CREATE_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  /** Ted #1208 — the grooming menu is open under the Grooming row. */
  const [groomMenuOpen, setGroomMenuOpen] = useState(false);
  /**
   * Kate K9 — a name that looks like an existing row's, waiting on the
   * owner's answer. `formId` is the row being edited or 'create'.
   */
  const [dupWarning, setDupWarning] = useState<{ formId: string; existing: string } | null>(null);
  const paymentMessages = useServicePaymentMessages(apiUrl);
  const { applySaved: applySavedPaymentMessage } = paymentMessages;
  const paymentEntries = useMemo(
    () =>
      paymentMessages.state.status === 'ready'
        ? new Map(paymentMessages.state.view.services.map((e) => [e.serviceTypeId, e]))
        : null,
    [paymentMessages.state],
  );

  const onPaymentMessageSaved = useCallback(
    (entry: ServicePaymentMessageEntry, serviceName: string) => {
      applySavedPaymentMessage(entry);
      setSaved(
        entry.message
          ? `${serviceName}: saved. Clients see your message on their next booking.`
          : `${serviceName}: back to the default wording.`,
      );
    },
    [applySavedPaymentMessage],
  );

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial'): Promise<boolean> => {
      if (mode === 'initial') {
        setStatus('loading');
        setLoadError(null);
        setSaved(null);
      }
      const res = await adminTenantServicesApi.list(apiUrl);
      if (res.error || !res.data) {
        if (mode === 'initial') {
          setLoadError(res.error ?? 'Could not load your services.');
          setStatus('error');
        }
        return false;
      }
      setView(res.data);
      setStatus('ready');
      return true;
    },
    [apiUrl],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(
    () => [...(view?.services ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName)),
    [view],
  );
  const nameById = useMemo(() => new Map(rows.map((s) => [s.name, s.displayName])), [rows]);
  const creatable = view?.creatable ?? [];
  const selectedOption = creatable.find((c) => c.name === createForm.name) ?? null;

  const clearTransient = () => {
    setRowError(null);
    setSaved(null);
    setConfirmHideId(null);
  };

  const startEdit = useCallback((s: TenantServiceView) => {
    setEditingId(s.id);
    setEditForm(editFormFromService(s));
    setPriceEditId(null);
    setRowError(null);
    setSaved(null);
    setConfirmHideId(null);
    setDupWarning(null);
  }, []);

  const saveEdit = useCallback(
    async (s: TenantServiceView, keepBoth = false) => {
      if (!editForm) return;
      const parsed = patchFromEditForm(editForm, s);
      if (!parsed.ok) {
        setRowError({ id: s.id, message: parsed.message });
        return;
      }
      if (Object.keys(parsed.patch).length === 0) {
        setEditingId(null);
        return;
      }
      // Kate K9 — a RENAME that looks like another row asks once; "Keep both" proceeds.
      if (parsed.patch.displayName !== undefined && !keepBoth) {
        const dup = findNearDuplicateName(parsed.patch.displayName, rows, s.id);
        if (dup) {
          setDupWarning({ formId: s.id, existing: dup.displayName });
          return;
        }
      }
      setDupWarning(null);
      setBusyId(s.id);
      setRowError(null);
      const res = await adminTenantServicesApi.update(apiUrl, s.id, parsed.patch);
      if (res.error || !res.data) {
        setBusyId(null);
        setRowError({ id: s.id, message: res.error ?? 'Could not save that service.' });
        return;
      }
      await load('refresh');
      setBusyId(null);
      setEditingId(null);
      setSaved(`${res.data.service.displayName}: saved. Customers see it on their next booking.`);
    },
    [apiUrl, editForm, load, rows],
  );

  const savePrice = useCallback(
    async (s: TenantServiceView) => {
      const cents = dollarInputToCents(priceInput);
      if (cents === null) {
        setRowError({ id: s.id, message: 'Enter the price in dollars, for example 47 or 47.50.' });
        return;
      }
      if (cents === s.basePriceCents) {
        setPriceEditId(null);
        return;
      }
      setBusyId(s.id);
      setRowError(null);
      const res = await adminTenantServicesApi.updatePrice(apiUrl, s.id, cents);
      if (res.error || !res.data) {
        setBusyId(null);
        setRowError({ id: s.id, message: res.error ?? 'Could not change that price.' });
        return;
      }
      await load('refresh');
      setBusyId(null);
      setPriceEditId(null);
      setSaved(`${s.displayName} is now ${formatDollars(cents)}. The next booking is charged the new price.`);
    },
    [apiUrl, load, priceInput],
  );

  // #921 — one tap on a swatch is one PATCH { color } on the general route.
  const saveColor = useCallback(
    async (s: TenantServiceView, color: string | null) => {
      if (color === (s.color ?? null)) {
        setColorEditId(null);
        return;
      }
      setBusyId(s.id);
      setRowError(null);
      setSaved(null);
      const res = await adminTenantServicesApi.update(apiUrl, s.id, { color });
      if (res.error || !res.data) {
        setBusyId(null);
        setRowError({ id: s.id, message: res.error ?? 'Could not change that color.' });
        return;
      }
      await load('refresh');
      setBusyId(null);
      setColorEditId(null);
      setSaved(
        isServiceColorKey(color)
          ? `${s.displayName} is ${SERVICE_COLORS[color].label.toLowerCase()} on the daily rollup, the day list and the arrivals list.`
          : `${s.displayName} has no color — it reads neutral on every list.`,
      );
    },
    [apiUrl, load],
  );

  const setActive = useCallback(
    async (s: TenantServiceView, isActive: boolean) => {
      setBusyId(s.id);
      setRowError(null);
      setSaved(null);
      const res = await adminTenantServicesApi.update(apiUrl, s.id, { isActive });
      setConfirmHideId(null);
      if (res.error || !res.data) {
        setBusyId(null);
        setRowError({ id: s.id, message: res.error ?? 'Could not change that service.' });
        return;
      }
      await load('refresh');
      setBusyId(null);
      setSaved(
        isActive
          ? `${s.displayName} is offered again. Customers can book it now.`
          : `${s.displayName} is hidden from customers. Existing bookings are unaffected.`,
      );
    },
    [apiUrl, load],
  );

  const openCreate = useCallback(() => {
    const first = creatable.find((c) => !c.alreadyAdded);
    setCreateForm({
      ...EMPTY_CREATE_FORM,
      name: first?.name ?? '',
      displayName: first?.defaultDisplayName ?? '',
      duration: first?.defaultDurationMinutes === null || first === undefined ? '' : String(first.defaultDurationMinutes),
    });
    setCreateError(null);
    setDupWarning(null);
    setCreateOpen(true);
    clearTransient();
  }, [creatable]);

  const pickKind = useCallback(
    (name: string) => {
      const option = creatable.find((c) => c.name === name);
      setCreateForm((f) => ({
        ...f,
        name,
        displayName: option ? option.defaultDisplayName : f.displayName,
        duration: option && option.defaultDurationMinutes !== null ? String(option.defaultDurationMinutes) : '',
        capacity: option?.pooled || option?.legOnly ? '' : f.capacity,
      }));
      setCreateError(null);
    },
    [creatable],
  );

  const submitCreate = useCallback(async (keepBoth = false) => {
    const parsed = draftFromCreateForm(createForm, creatable);
    if (!parsed.ok) {
      setCreateError(parsed.message);
      return;
    }
    // Kate K9 — a new name that looks like an existing row asks once; never a block.
    if (!keepBoth) {
      const dup = findNearDuplicateName(parsed.draft.displayName, rows);
      if (dup) {
        setDupWarning({ formId: 'create', existing: dup.displayName });
        return;
      }
    }
    setDupWarning(null);
    setIsCreating(true);
    setCreateError(null);
    const res = await adminTenantServicesApi.create(apiUrl, parsed.draft);
    setIsCreating(false);
    if (res.error || !res.data) {
      setCreateError(res.error ?? 'Could not add that service.');
      return;
    }
    const { service, rule } = res.data;
    setCreateOpen(false);
    await load('refresh');
    setSaved(
      `Added ${service.displayName} at ${formatDollars(service.basePriceCents)}${
        rule ? ` with a daily limit of ${rule.maxCapacity}` : service.pooledInto ? ` (shares the ${nameById.get(service.pooledInto) ?? service.pooledInto} limit)` : ''
      }. Customers can book it now.`,
    );
  }, [apiUrl, createForm, creatable, load, nameById, rows]);

  /** Kate K9 — the soft warning block: the question, Keep both, Change the name. */
  const dupWarningFor = (formId: string, onKeepBoth: () => void, busy: boolean) =>
    dupWarning?.formId === formId ? (
      <div className="ferro-services__dup" data-testid={`service-dup-${formId}`}>
        <Alert variant="warning">{nearDuplicateQuestion(dupWarning.existing)}</Alert>
        <div className="ferro-capacity__actions">
          <Button type="button" size="sm" variant="outline" isLoading={busy} onClick={onKeepBoth} data-testid={`service-dup-keep-${formId}`}>
            Keep both
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setDupWarning(null)} data-testid={`service-dup-rename-${formId}`}>
            Change the name
          </Button>
        </div>
      </div>
    ) : null;

  // ── Loading ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <Card title="Services & prices">
        <div data-testid="services-loading">
          <Skeleton variant="text" lines={5} />
        </div>
      </Card>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (status === 'error' || !view) {
    return (
      <Card title="Services & prices">
        <div data-testid="services-error">
          <Alert variant="error">{loadError ?? 'Could not load your services.'}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const canAdd = canEditPrice && creatable.some((c) => !c.alreadyAdded);
  const readOnly = !canEdit && !canEditPrice;

  return (
    <Card
      title="Services & prices"
      subtitle="What customers can book, what they pay and how long it takes. Change it here and the booking page follows."
      headerRight={
        canAdd ? (
          <Button variant="primary" onClick={openCreate} data-testid="services-add">
            Add a service
          </Button>
        ) : undefined
      }
    >
      <div className="ferro-settings__card" data-testid="services-ready">
        {rows.length === 0 ? (
          <div data-testid="services-empty">
            <EmptyState
              title="No services yet"
              description="Add the first thing customers can book — daycare, boarding, grooming or a walk — with its price and daily limit."
              action={canAdd ? { label: 'Add a service', onClick: openCreate } : undefined}
            />
          </div>
        ) : (
          <>
            {saved ? (
              <div data-testid="services-saved">
                <Alert variant="success">{saved}</Alert>
              </div>
            ) : null}
            {paymentMessages.state.status === 'error' ? (
              <div className="ferro-services__pay-load-error" data-testid="service-pay-load-error">
                <Alert variant="warning">
                  Could not load your after-booking messages. Clients still see what was last saved.
                </Alert>
                <div className="ferro-settings__actions">
                  <Button variant="outline" size="sm" onClick={paymentMessages.reload} data-testid="service-pay-retry">
                    Try again
                  </Button>
                </div>
              </div>
            ) : null}
            <ul className="ferro-services__list" data-testid="services-list">
              {rows.map((s) => {
                const busy = busyId === s.id;
                const err = rowError?.id === s.id ? rowError.message : null;
                const isEditing = editingId === s.id && editForm !== null;
                const isPricing = priceEditId === s.id;
                const isColoring = colorEditId === s.id;
                const colorLabel = isServiceColorKey(s.color) ? SERVICE_COLORS[s.color].label : 'No color';
                return (
                  <li
                    key={s.id}
                    className={['ferro-services__row', s.isActive ? '' : 'ferro-services__row--hidden', serviceColorClass(s.color)]
                      .filter(Boolean)
                      .join(' ')}
                    data-testid={`service-row-${s.id}`}
                    data-color={s.color ?? ''}
                  >
                    <div className="ferro-services__main">
                      <div className="ferro-services__name" data-testid={`service-display-${s.id}`}>
                        {s.displayName}
                      </div>
                      <div className="ferro-services__facts">
                        {isPricing ? (
                          <span className="ferro-capacity__inline">
                            <Input
                              type="text"
                              inputMode="decimal"
                              prefix="$"
                              value={priceInput}
                              onChange={(e) => setPriceInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void savePrice(s);
                                } else if (e.key === 'Escape') setPriceEditId(null);
                              }}
                              aria-label={`${s.displayName} price in dollars`}
                              ref={priceInputRef}
                              data-testid={`service-price-input-${s.id}`}
                            />
                            <Button size="sm" variant="primary" isLoading={busy} onClick={() => void savePrice(s)} data-testid={`service-price-save-${s.id}`}>
                              Save price
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPriceEditId(null)}>
                              Cancel
                            </Button>
                          </span>
                        ) : (
                          <span className="ferro-services__price" data-testid={`service-price-${s.id}`}>
                            {formatDollars(s.basePriceCents)}
                          </span>
                        )}
                        <span className="ferro-muted" data-testid={`service-duration-${s.id}`}>
                          {durationLabel(s.durationMinutes)}
                        </span>
                        <span data-testid={`service-status-${s.id}`}>
                          <Badge variant={s.isActive ? 'active' : 'closed'}>{s.isActive ? 'Offered' : 'Hidden'}</Badge>
                        </span>
                        <span className="ferro-muted" data-testid={`service-limit-${s.id}`}>
                          {s.pooledInto
                            ? `Shares the ${nameById.get(s.pooledInto) ?? s.pooledInto} limit`
                            : s.everyDayCap === null
                              ? 'Daily limit not set'
                              : `${s.everyDayCap} per day`}
                        </span>
                        {s.runDays ? (
                          <span className="ferro-muted" data-testid={`service-run-days-${s.id}`}>
                            Runs {describeWeekdays(s.runDays)}
                          </span>
                        ) : null}
                        <span
                          className={['ferro-service-chip', serviceColorClass(s.color)].filter(Boolean).join(' ')}
                          data-testid={`service-color-${s.id}`}
                        >
                          {colorLabel}
                        </span>
                      </div>
                      <div className="ferro-muted ferro-services__behavior">{s.behavior}</div>
                      {/* Kate K7 — what clients read under the name, or an honest "none yet". */}
                      {s.description ? (
                        <p className="ferro-services__description-preview" data-testid={`service-description-${s.id}`}>
                          {s.description}
                        </p>
                      ) : (
                        <p className="ferro-muted ferro-services__note" data-testid={`service-description-${s.id}`}>
                          No description yet — clients see a generic line.
                        </p>
                      )}
                      {s.name === GROOMING_SERVICE_NAME ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ferro-services__menu-toggle"
                          aria-expanded={groomMenuOpen}
                          aria-controls={`groom-menu-${s.id}`}
                          onClick={() => setGroomMenuOpen((open) => !open)}
                          data-testid="service-groom-menu-toggle"
                        >
                          {groomMenuOpen ? 'Close grooming menu' : 'Grooming menu and prices'}
                        </Button>
                      ) : null}
                    </div>

                    {readOnly || isEditing || isColoring ? null : (
                      <div className="ferro-services__actions">
                        {canEdit ? (
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => startEdit(s)} data-testid={`service-edit-${s.id}`}>
                            Edit
                          </Button>
                        ) : null}
                        {canEdit ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => {
                              setColorEditId(s.id);
                              setEditingId(null);
                              setPriceEditId(null);
                              clearTransient();
                            }}
                            data-testid={`service-color-edit-${s.id}`}
                          >
                            Color
                          </Button>
                        ) : null}
                        {canEditPrice && !isPricing ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => {
                              setPriceEditId(s.id);
                              setPriceInput(centsToDollarInput(s.basePriceCents));
                              setEditingId(null);
                              clearTransient();
                            }}
                            data-testid={`service-price-edit-${s.id}`}
                          >
                            Change price
                          </Button>
                        ) : null}
                        {canEdit ? (
                          s.isActive ? (
                            confirmHideId === s.id ? (
                              <>
                                <Button size="sm" variant="danger" isLoading={busy} onClick={() => void setActive(s, false)} data-testid={`service-hide-confirm-${s.id}`}>
                                  Hide from customers
                                </Button>
                                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmHideId(null)}>
                                  Keep
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setConfirmHideId(s.id); setSaved(null); }} data-testid={`service-hide-${s.id}`}>
                                Hide
                              </Button>
                            )
                          ) : (
                            <Button size="sm" variant="ghost" isLoading={busy} onClick={() => void setActive(s, true)} data-testid={`service-show-${s.id}`}>
                              Offer again
                            </Button>
                          )
                        ) : null}
                      </div>
                    )}

                    {isColoring ? (
                      <div
                        className="ferro-services__colors"
                        role="group"
                        aria-label={`Color for ${s.displayName}`}
                        data-testid={`service-color-picker-${s.id}`}
                      >
                        <p className="ferro-muted ferro-services__note">
                          Pick the color {s.displayName} wears on the daily rollup, the day list and the arrivals list. One tap saves it.
                        </p>
                        <div className="ferro-services__swatches">
                          {SERVICE_COLOR_KEYS.map((key) => (
                            <button
                              key={key}
                              type="button"
                              className={`ferro-services__swatch ${serviceColorClass(key)}`}
                              aria-label={SERVICE_COLORS[key].label}
                              aria-pressed={s.color === key}
                              disabled={busy}
                              onClick={() => void saveColor(s, key)}
                              data-testid={`service-color-swatch-${s.id}-${key}`}
                            >
                              <span className="ferro-services__swatch-dot" aria-hidden="true" />
                              <span className="ferro-services__swatch-label">{SERVICE_COLORS[key].label}</span>
                            </button>
                          ))}
                          <button
                            type="button"
                            className="ferro-services__swatch ferro-services__swatch--none"
                            aria-pressed={!s.color}
                            disabled={busy}
                            onClick={() => void saveColor(s, null)}
                            data-testid={`service-color-none-${s.id}`}
                          >
                            No color
                          </button>
                        </div>
                        <div className="ferro-capacity__actions">
                          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setColorEditId(null)} data-testid={`service-color-done-${s.id}`}>
                            Done
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    {confirmHideId === s.id ? (
                      <p className="ferro-muted ferro-services__note" data-testid={`service-hide-note-${s.id}`}>
                        Customers stop seeing {s.displayName}. Existing bookings are unaffected, and you can offer it again any time.
                      </p>
                    ) : null}

                    {isEditing && editForm ? (
                      <form
                        className="ferro-form ferro-services__edit"
                        noValidate
                        onSubmit={(e) => {
                          e.preventDefault();
                          void saveEdit(s);
                        }}
                        aria-label={`Edit ${s.displayName}`}
                        data-testid={`service-edit-form-${s.id}`}
                      >
                        <div className="ferro-settings__form-grid">
                          <Input
                            id={`service-name-${s.id}`}
                            label="Name customers see"
                            value={editForm.displayName}
                            maxLength={80}
                            onChange={(e) => setEditForm((f) => (f ? { ...f, displayName: e.target.value } : f))}
                            data-testid={`service-name-${s.id}`}
                          />
                          <DescriptionField
                            id={`service-description-input-${s.id}`}
                            value={editForm.description}
                            onChange={(v) => setEditForm((f) => (f ? { ...f, description: v } : f))}
                            testId={`service-description-input-${s.id}`}
                          />
                          {editForm.runDays !== undefined && !isLegOnlyServiceName(s.name) ? (
                            <RunDaysField
                              id={`service-run-days-input-${s.id}`}
                              days={editForm.runDays}
                              disabled={busy}
                              onChange={(next) => setEditForm((f) => (f ? { ...f, runDays: next } : f))}
                            />
                          ) : null}
                          <Input
                            id={`service-duration-input-${s.id}`}
                            label="Duration (minutes)"
                            type="number"
                            inputMode="numeric"
                            min={5}
                            max={1440}
                            step={5}
                            placeholder="Whole day"
                            value={editForm.duration}
                            onChange={(e) => setEditForm((f) => (f ? { ...f, duration: e.target.value } : f))}
                            helperText="Leave blank for a whole-day service."
                            data-testid={`service-duration-input-${s.id}`}
                          />
                          <Input
                            id={`service-sort-${s.id}`}
                            label="Order on the booking page"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={999}
                            step={1}
                            value={editForm.sortOrder}
                            onChange={(e) => setEditForm((f) => (f ? { ...f, sortOrder: e.target.value } : f))}
                            helperText="Lower numbers show first."
                            data-testid={`service-sort-${s.id}`}
                          />
                          <Input
                            id={`service-notice-${s.id}`}
                            label="Minimum notice (hours)"
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step={1}
                            placeholder="No minimum"
                            value={editForm.notice}
                            onChange={(e) => setEditForm((f) => (f ? { ...f, notice: e.target.value } : f))}
                            data-testid={`service-notice-${s.id}`}
                          />
                          <Input
                            id={`service-window-${s.id}`}
                            label="Booking window (days ahead)"
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={730}
                            step={1}
                            placeholder="No limit"
                            value={editForm.window}
                            onChange={(e) => setEditForm((f) => (f ? { ...f, window: e.target.value } : f))}
                            data-testid={`service-window-${s.id}`}
                          />
                        </div>
                        {dupWarningFor(s.id, () => void saveEdit(s, true), busy)}
                        <div className="ferro-capacity__actions">
                          <Button type="submit" size="sm" variant="primary" isLoading={busy} data-testid={`service-save-${s.id}`}>
                            Save
                          </Button>
                          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => { setEditingId(null); setDupWarning(null); }} data-testid={`service-cancel-${s.id}`}>
                            Cancel
                          </Button>
                        </div>
                      </form>
                    ) : null}

                    {paymentMessages.state.status === 'loading' ? (
                      <div className="ferro-services__pay" data-testid={`service-pay-loading-${s.id}`}>
                        <Skeleton variant="text" lines={1} />
                      </div>
                    ) : null}
                    {paymentMessages.state.status === 'ready' && paymentEntries?.get(s.id) ? (
                      <ServicePaymentMessageField
                        apiUrl={apiUrl}
                        serviceName={s.displayName}
                        entry={paymentEntries.get(s.id)!}
                        maxLength={paymentMessages.state.view.limits.maxLength}
                        onlinePayments={paymentMessages.state.view.onlinePayments}
                        canEdit={canEdit}
                        onSaved={onPaymentMessageSaved}
                      />
                    ) : null}

                    {err ? (
                      <div className="ferro-services__error" data-testid={`service-row-error-${s.id}`}>
                        <Alert variant="error">{err}</Alert>
                      </div>
                    ) : null}

                    {s.name === GROOMING_SERVICE_NAME && groomMenuOpen ? (
                      <div id={`groom-menu-${s.id}`} className="ferro-services__groom-menu">
                        <GroomingMenuEditor apiUrl={apiUrl} canEdit={canEditPrice} />
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {readOnly ? (
          <p className="ferro-muted" data-testid="services-readonly-note">
            Only an owner or manager can change services. Only an owner can change prices.
          </p>
        ) : !canEditPrice ? (
          <p className="ferro-muted" data-testid="services-price-note">
            Prices and new services are an owner action.
          </p>
        ) : null}
      </div>

      <Modal
        isOpen={createOpen}
        onClose={() => {
          if (!isCreating) setCreateOpen(false);
        }}
        title="Add a service"
        closeOnBackdropClick={!isCreating}
        closeOnEsc={!isCreating}
      >
        <form
          className="ferro-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submitCreate();
          }}
          aria-label="Add a service"
          data-testid="service-form"
        >
          <Select
            name="serviceKind"
            label="Kind of service"
            value={createForm.name}
            onChange={(e) => pickKind(e.target.value)}
            placeholder="Choose…"
            options={creatable.map((c) => ({
              value: c.name,
              label: c.alreadyAdded ? `${c.label} — already in your catalog` : c.label,
              disabled: c.alreadyAdded,
            }))}
            helperText="The kind decides how the booking engine treats it; the name below is what customers see."
            data-testid="service-form-kind"
          />
          {selectedOption ? (
            <p className="ferro-muted ferro-settings__footnote" data-testid="service-form-behavior">
              {selectedOption.behavior}
            </p>
          ) : null}
          <Input
            name="serviceDisplayName"
            label="Name customers see"
            maxLength={80}
            value={createForm.displayName}
            onChange={(e) => setCreateForm((f) => ({ ...f, displayName: e.target.value }))}
            placeholder="Walk 30 min"
            data-testid="service-form-display"
          />
          <DescriptionField
            id="service-form-description"
            value={createForm.description}
            onChange={(v) => setCreateForm((f) => ({ ...f, description: v }))}
            testId="service-form-description"
          />
          <div className="ferro-settings__form-grid">
            <Input
              name="servicePrice"
              label="Price"
              type="text"
              inputMode="decimal"
              prefix="$"
              value={createForm.price}
              onChange={(e) => setCreateForm((f) => ({ ...f, price: e.target.value }))}
              placeholder="30.00"
              data-testid="service-form-price"
            />
            <Input
              name="serviceDuration"
              label="Duration (minutes)"
              type="number"
              inputMode="numeric"
              min={5}
              max={1440}
              step={5}
              placeholder="Whole day"
              value={createForm.duration}
              onChange={(e) => setCreateForm((f) => ({ ...f, duration: e.target.value }))}
              helperText="Leave blank for a whole-day service."
              data-testid="service-form-duration"
            />
            {selectedOption && !selectedOption.pooled && !selectedOption.legOnly ? (
              <Input
                name="serviceCapacity"
                label="Daily limit"
                type="number"
                inputMode="numeric"
                min={0}
                max={MAX_SERVICE_CAPACITY}
                step={1}
                value={createForm.capacity}
                onChange={(e) => setCreateForm((f) => ({ ...f, capacity: e.target.value }))}
                helperText="How many per day. Without a limit customers could never book it."
                data-testid="service-form-capacity"
              />
            ) : null}
          </div>
          {createError ? (
            <div data-testid="service-form-error">
              <Alert variant="error">{createError}</Alert>
            </div>
          ) : null}
          {dupWarningFor('create', () => void submitCreate(true), isCreating)}
          <div className="ferro-form__actions">
            <Button type="submit" variant="primary" isLoading={isCreating} data-testid="service-form-create">
              Add service
            </Button>
            <Button type="button" variant="ghost" disabled={isCreating} onClick={() => { setCreateOpen(false); setDupWarning(null); }}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}
